import { FC, useState, ReactNode, useCallback, useMemo } from "react";
import Image from "next/image";
import { Trans, t } from "@lingui/macro";
import Link from "next/link";
import { Text } from "@otrium/atoms";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import {
  HomepageCategorySectionData,
  HomepageSingleCategoryProduct,
} from "src/types/graphql.d";
import { Carousel } from "src/molecules/Carousel";
import { ProductCardWishlist } from "src/organisms/ProductCardWishlist";
import { LAST_TILE_MEDIA } from "src/molecules/ProductCard";
import { cmsImageLoader } from "src/utils/cms-image-loader";
import { Skeleton } from "src/atoms/Skeleton";
import { useImageLoaded } from "src/hooks/use-image-loaded";
import { HyperLink } from "src/atoms/HyperLink";
import styles from "./CuratedCategory.module.scss";
import { formatName } from "src/utils/formatName";
import { CarouselOverlay } from "src/molecules/CarouselOverlay";
import { SEGMENT_LIST_FORMAT, SEGMENT_LIST_TYPE } from "src/segment";
import { useImageHeight } from "src/hooks/useImageHeight";
import { useTheme } from "@emotion/react";
import { GatedPopupTrackingProps } from "src/types/GatedPopup";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { useLingui } from "@lingui/react";
import { LoginRegisterModal } from "../LoginRegisterModal";
import {
  deleteSessionStorageItem,
  setSessionStorageJSON,
} from "src/utils/helper/sessionStorage";
import { GATED_POPUP_REDIRECT_DATA } from "src/constants/gatedItem";
import { ProductRedirect } from "src/organisms/ProductCardWishlist/ProductCardWishlist";
import { ChevronTextSmall } from "src/icons/ChevronTextSmall";
import { CAROUSEL_CONTENT_TYPE } from "src/utils/constants";
import { trackCarouselViewedEvent } from "src/tracking/events/carousel-viewed";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { trackCarouselViewAllSelectedEvent } from "src/tracking/events/carousel-view-all-selected";
import { trackCarouselItemSelectedEvent } from "src/tracking/events/carousel-item-selected";
import { useIsClient } from "src/hooks/use-is-client";

interface CuratedCategoryHeaderTitleProps {
  title: string;
  subTitle: string;
}

const LinkWrapper: FC<{
  href?: string;
  children: ReactNode;
  onClick?: () => void;
}> = ({ href, children, onClick }) => {
  return href ? (
    <Link href={href} onClick={onClick} prefetch={false}>
      {children}
    </Link>
  ) : (
    <Box onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined}>
      {children}
    </Box>
  );
};

const CuratedCategoryHeaderTitle: FC<CuratedCategoryHeaderTitleProps> = ({
  title,
  subTitle,
}) => {
  return (
    <Box pb={[3, 3, 3, 0, 0]} pr={[3, 3, 3, 0, 0]}>
      {subTitle && (
        <Text
          as="h3"
          sx={{
            color: "#999",
            fontWeight: "normal",
            fontSize: "20px",
            textTransform: "uppercase",
          }}
        >
          {formatName(subTitle)}
        </Text>
      )}
      <Flex>
        <Text mb={[0, 0, 0, 2]} as="h3" sx={{ textTransform: "uppercase" }}>
          {formatName(title)}
          <ChevronTextSmall />
        </Text>
      </Flex>
    </Box>
  );
};

interface CuratedCategoryHeaderProps {
  title: string;
  subTitle: string;
  href?: string;
  as?: string;
  category: HomepageCategorySectionData;
  viewAllOnClick?: () => void;
  isGated?: boolean;
}

const CuratedCategoryHeader: FC<CuratedCategoryHeaderProps> = ({
  title,
  subTitle,
  href,
  category,
  viewAllOnClick,
}) => {
  const theme = useTheme();
  const { isMobile } = useBreakpoints();
  const isClient = useIsClient();
  const { imgRef, loaded, onLoad } = useImageLoaded();

  const bannerSrc =
    isClient && isMobile ? category.banner_image_mobile : category.banner_image;

  return (
    <LinkWrapper href={href} onClick={viewAllOnClick || undefined}>
      <div className={styles.header}>
        <div className={styles.imageContainer}>
          {bannerSrc && (
            <>
              {!loaded && (
                <Skeleton style={{ position: "absolute", inset: 0 }} />
              )}
              <Image
                ref={imgRef}
                loader={cmsImageLoader}
                src={bannerSrc}
                alt={title}
                fill
                sizes="100vw"
                onLoad={onLoad}
                style={{
                  objectFit: "cover",
                  objectPosition: "top",
                  opacity: loaded ? 1 : 0,
                  transition: "opacity 300ms ease",
                }}
              />
            </>
          )}
        </div>

        <div className={styles.titleWrapper}>
          <CuratedCategoryHeaderTitle title={title} subTitle={subTitle} />
          <HyperLink
            className={styles.viewAll}
            hasUnderline
            onClick={viewAllOnClick}
            color={theme.colors.tone.white}
          >
            <Trans>View all</Trans>
          </HyperLink>
        </div>
      </div>
    </LinkWrapper>
  );
};

interface CuratedCategoryProps {
  genderId: string;
  category: HomepageCategorySectionData;
  slidesDesktop?: number;
  slidesTablet?: number;
  slidesMobile?: number;
  trackingData?: GatedPopupTrackingProps;
  carouselId: string;
  carouselPosition: number;
}

function CuratedCategory({
  category,
  genderId,
  slidesDesktop,
  slidesTablet,
  slidesMobile,
  trackingData,
  carouselId,
  carouselPosition,
}: CuratedCategoryProps): JSX.Element {
  const [imageElementWrapperHeight, getImageElementWrapper] = useImageHeight();
  const [open, setOpen] = useState<boolean>(false);
  const isLoggedIn = useIsLoggedIn();
  const { enableGatedHomepage } = useFeatureFlags();
  const { i18n } = useLingui();
  const { isMobile } = useBreakpoints();
  const { currentShopType } = useShopTypeContext();

  const allProducts = useMemo(
    () =>
      category.products?.length
        ? !isLoggedIn && enableGatedHomepage
          ? [
              ...category.products.slice(0, -1),
              {
                slug: "last-tile",
                id: "last-tile",
                name: t(i18n)`Log in`,
                brand: {
                  name: t(i18n)`Log in to unlock even more fashion brands`,
                },
                thumbnail: LAST_TILE_MEDIA.poster,
              },
            ]
          : category.products
        : [],
    [category.products, isLoggedIn, enableGatedHomepage, i18n]
  );

  const mappedTrackingData = useMemo(() => {
    return {
      // The real products, not allProducts: the gated variant swaps the last
      // tile for a "Log in" placeholder that is not a trackable item.
      items: category.products || [],
      contentType: CAROUSEL_CONTENT_TYPE.PRODUCT,
      carouselId,
      carouselPosition,
      shopType: currentShopType,
      gated: !isLoggedIn && !!enableGatedHomepage,
      title: category.title,
      subtitle: category.subtitle,
      targetLink: category.url,
      imageUrl:
        isMobile && category.banner_image_mobile
          ? category.banner_image_mobile
          : category.banner_image,
    };
  }, [
    isMobile,
    currentShopType,
    category,
    carouselId,
    carouselPosition,
    isLoggedIn,
    enableGatedHomepage,
  ]);

  const listFormat = SEGMENT_LIST_FORMAT.carousel;
  const listId = category.title;
  const listType = SEGMENT_LIST_TYPE.curatedCategory;
  const { pageName, shopType, carouselType } =
    (trackingData as GatedPopupTrackingProps) || {};

  const viewAllLink =
    category.url || `/collections/${genderId}/${category.slug}`;

  const handleViewAllClick = useCallback(() => {
    if (!isLoggedIn && enableGatedHomepage) {
      setOpen(true);
      return;
    }

    void trackCarouselViewAllSelectedEvent({ ...mappedTrackingData });
  }, [enableGatedHomepage, isLoggedIn, mappedTrackingData]);

  const handleProductClick = useCallback(
    (itemId: string) => {
      void trackCarouselItemSelectedEvent({ ...mappedTrackingData, itemId });
    },
    [mappedTrackingData]
  );

  const handleTrackCarouselViewed = useCallback(
    (visibleIndexes: number[]) => {
      void trackCarouselViewedEvent({
        ...mappedTrackingData,
        visibleIndexes,
      });
    },
    [mappedTrackingData]
  );

  const setRedirectData = useCallback(
    (type: string, product?: ProductRedirect, actionSource?: string) => {
      deleteSessionStorageItem(GATED_POPUP_REDIRECT_DATA);
      setSessionStorageJSON(GATED_POPUP_REDIRECT_DATA, {
        gatedActionType: type,
        numberOfItems: category.products?.length || 0,
        product,
        productListMeta: {
          listFormat,
          listId,
          listType,
          shopType: genderId,
        },
        actionSource: actionSource || "view_all",
      });
    },
    [category.products?.length, listFormat, listId, listType, genderId]
  );
  const showGatedBehaviour = enableGatedHomepage ? isLoggedIn : true;

  const items = useMemo(
    () =>
      allProducts.map((product, index) => {
        if (
          allProducts &&
          index === allProducts.length - 1 &&
          showGatedBehaviour
        ) {
          return (
            <CarouselOverlay
              key={product.id}
              link={viewAllLink}
              onClick={handleViewAllClick}
            >
              <ProductCardWishlist
                centerText={true}
                key={product.id}
                position={index}
                product={product as HomepageSingleCategoryProduct}
                onProductClickTracker={handleProductClick}
                showLabels={false}
                showName={false}
                showSizes={false}
                getImageElementWrapper={getImageElementWrapper}
                trackingData={trackingData}
                showHoverBorder={true}
              />
            </CarouselOverlay>
          );
        }

        return (
          <ProductCardWishlist
            centerText={true}
            key={product.id}
            position={index}
            product={product as HomepageSingleCategoryProduct}
            onProductClickTracker={handleProductClick}
            showLabels={false}
            showName={false}
            showSizes={false}
            redirectLink={viewAllLink}
            getImageElementWrapper={getImageElementWrapper}
            trackingData={trackingData}
            setProductRedirect={setRedirectData}
            showHoverBorder={true}
          />
        );
      }),
    [
      allProducts,
      showGatedBehaviour,
      viewAllLink,
      getImageElementWrapper,
      trackingData,
      setRedirectData,
      handleProductClick,
      handleViewAllClick,
    ]
  );

  return (
    <Box backgroundColor="#FFFFFF">
      <CuratedCategoryHeader
        title={category.title}
        subTitle={category.subtitle}
        href={showGatedBehaviour ? viewAllLink : undefined}
        category={category}
        viewAllOnClick={handleViewAllClick}
        isGated={!isLoggedIn && !!enableGatedHomepage}
      />
      {category.products && (
        <Carousel
          imageElementWrapperHeight={imageElementWrapperHeight}
          items={items}
          slidesDesktop={slidesDesktop}
          slidesTablet={slidesTablet}
          slidesMobile={slidesMobile}
          marginSizeMobile="l"
          trackCarouselViewed={handleTrackCarouselViewed}
        />
      )}
      {open && (
        <LoginRegisterModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          redirectTo={viewAllLink}
          trackingData={{
            pageName,
            shopType,
            carouselType,
            actionSource: "view_all",
            redirectTo: viewAllLink,
          }}
          onRedirect={setRedirectData}
        />
      )}
    </Box>
  );
}

export default CuratedCategory;
